"use client"

import { useState, useEffect, useCallback } from "react";
import { Card } from "react-bootstrap";
import { PieChart, Pie, Sector, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import XLSX from 'xlsx';
import { toast } from "react-toastify";

import * as myApi from "../Api";
const renderActiveShape = (props, isDarkMode) => {
    const RADIAN = Math.PI / 180;
    const { cx, cy, midAngle, innerRadius, outerRadius, startAngle, endAngle, fill, payload, percent, value } = props;
    const sin = Math.sin(-RADIAN * midAngle);
    const cos = Math.cos(-RADIAN * midAngle);
    const sx = cx + (outerRadius + 10) * cos;
    const sy = cy + (outerRadius + 10) * sin;
    const mx = cx + (outerRadius + 30) * cos;
    const my = cy + (outerRadius + 30) * sin;
    const ex = mx + (cos >= 0 ? 1 : -1) * 22;
    const ey = my;
    const textAnchor = cos >= 0 ? 'start' : 'end';
    const textColor = isDarkMode ? "#fff" : "#333";
    const subTextColor = isDarkMode ? "#aaa" : "#999";

    return (
      <g>
        <text x={cx} y={cy} dy={8} textAnchor="middle" fill={fill}>
          {payload.name}
        </text>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
        />
        <Sector
          cx={cx}
          cy={cy}
          startAngle={startAngle}
          endAngle={endAngle}
          innerRadius={outerRadius + 6}
          outerRadius={outerRadius + 10}
          fill={fill}
        />
        <path d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`} stroke={fill} fill="none" />
        <circle cx={ex} cy={ey} r={2} fill={fill} stroke="none" />
        <text x={ex + (cos >= 0 ? 1 : -1) * 12} y={ey} textAnchor={textAnchor} fill={textColor}>{`${payload.name} (${value})`}</text>
        <text x={ex + (cos >= 0 ? 1 : -1) * 12} y={ey} dy={18} textAnchor={textAnchor} fill={subTextColor}>
          {`(Rate ${(percent * 100).toFixed(2)}%)`}
        </text>
      </g>
    );
  };

const RADIAN = Math.PI / 180;
const renderCustomizedLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent,
  index
}) => {
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill="white"
      textAnchor={x > cx ? "start" : "end"}
      dominantBaseline="central"
    >
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

const UserStatusSection = ({ data }) => {
    const [_colors] = useState(["#0088FE", "#00C49F"]);
    const [isDarkMode, setIsDarkMode] = useState(false);

    const [users, setUsers] = useState({});
    const [activeIndex, setActiveIndex] = useState(0);
    const [activeCount, setActiveCount] = useState(0);
    const [inactiveCount, setInactiveCount] = useState(0);

    useEffect(() => {
        const checkDarkMode = () => {
            const htmlElement = document.querySelector("html");
            setIsDarkMode(htmlElement.classList.contains("dark") || localStorage.getItem("theme") === "dark");
        };
        checkDarkMode();
    }, []);

    const onPieEnter = useCallback(
        (_, index) => {
            setActiveIndex(index);
        },
        [setActiveIndex]
    );

    useEffect(() => {
        setUsers(data);
    }, [data]);

    useEffect(() => {
        const active = users.active
        const inactive = users.inactive
        setActiveCount(active);
        setInactiveCount(inactive);
    }, [users]);

    const [chartData, setChartData] = useState([
        { name: 'Active', value: 0 },
        { name: 'Inactive', value: 0 },
    ]);

    // add function to download users status as xlsx file with two tabs, Active and Inactive
    const downloadUsers = () => {
        // fetch data from /admin/user/list/ using axios
        myApi.post('/fetchUserStatus').then((res) => {
            let result = res.data

            let active = result.resultActive
            let inactive = result.resultInactive

            const wb = XLSX.utils.book_new();
            const ws1 = XLSX.utils.json_to_sheet(active);
            const ws2 = XLSX.utils.json_to_sheet(inactive);

            // // load first item and convert it to array
            // let sampleActive = active[0]
            // let sampleInactive = inactive[0]

            // let keysActive = Object.values(sampleActive)
            // let keysInactive = Object.keys(sampleInactive)

            // // loop through keys and set maxwidth
            // for (let i = 0; i < keysActive.length; i++) {
            //     const activeMaxWidth = active.reduce((w, r) => Math.max(w, keysActive[i].length), 10);
            //     ws1["!cols"] = [ { wch: activeMaxWidth } ];
            // }

            // for (let i = 0; i < keysInactive.length; i++) {
            //     const inactiveMaxWidth = inactive.reduce((w, r) => Math.max(w, keysInactive[i].length), 10);
            //     ws2["!cols"] = [ { wch: inactiveMaxWidth } ];
            // }

            /* calculate column width */

            // const activeMaxWidth = active.reduce((w, r) => Math.max(w, r.name.length), 10);
            // const inactiveMaxWidth = inactive.reduce((w, r) => Math.max(w, r.name.length), 10);
            // ws1["!cols"] = [ { wch: activeMaxWidth } ];
            // ws2["!cols"] = [ { wch: inactiveMaxWidth } ];
            
            XLSX.utils.book_append_sheet(wb, ws1, 'Active');
            XLSX.utils.book_append_sheet(wb, ws2, 'Inactive');
            XLSX.writeFile(wb, 'users.xlsx');
        }).catch(() => {
            toast("Failed to load user status", { type: "error" });
        })
    }

    useEffect(() => {
        setChartData([
            { name: 'Active', value: activeCount },
            { name: 'Inactive', value: inactiveCount },
        ]);
    }, [activeCount, inactiveCount]);
  
    return (
        <Card className="border-0">
            <Card.Header className={`bg-primary ${isDarkMode ? 'text-white' : 'text-white'}`}>
                User By Status
            </Card.Header>
            <Card.Body>
                <ResponsiveContainer width="100%" height={300}>
                    <PieChart width={300} height={300}>
                        <Pie
                            activeIndex={activeIndex}
                            activeShape={(props) => renderActiveShape(props, isDarkMode)}
                            label={renderCustomizedLabel}
                            dataKey="value"
                            startAngle={360}
                            endAngle={0}
                            data={chartData}
                            cx="50%"
                            cy="50%"
                            innerRadius={30}
                            outerRadius={90}
                            fill="#8884d8"
                            onMouseEnter={onPieEnter}
                        >
                            {chartData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={_colors[index % _colors.length]} />
                            ))}
                        </Pie>
                        <Tooltip 
                            contentStyle={{ 
                                backgroundColor: isDarkMode ? "#1a1a1a" : "#fff", 
                                border: isDarkMode ? "1px solid #444" : "1px solid #ccc",
                                color: isDarkMode ? "#fff" : "#333"
                            }} 
                        />
                    </PieChart>
                </ResponsiveContainer>
                <div className="d-flex justify-content-center gap-5 mt-3">
                    <div className="text-center">
                        <p className="mb-0 text-secondary">Inactive</p>
                        <h1 className="mb-0">{inactiveCount}</h1>
                    </div>
                    <div className="text-center">
                        <p className="mb-0 text-secondary">Active</p>
                        <h1 className="mb-0">{activeCount}</h1>
                    </div>
                </div>
            </Card.Body>
            <Card.Footer className="bg-transparent border-0">
                <div className="d-flex justify-content-center">
                    <button className="btn btn-primary" onClick={downloadUsers}>
                        <span className="text-white d-flex gap-2 align-items-center">
                            <i className="material-symbols-outlined">cloud_download</i>
                            <span> Save as XLSX</span>
                        </span>
                    </button>
                </div>
            </Card.Footer>
        </Card>
    );
};

export default UserStatusSection;